
import { createRxDatabase, removeRxDatabase, RxDatabase, addRxPlugin } from 'rxdb';
import { RxDBReplicationPlugin } from 'rxdb/plugins/replication';
import { RxDBReplicationGraphQLPlugin } from 'rxdb/plugins/replication-graphql';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
//import { RxDBValidatePlugin } from 'rxdb/plugins/validate';
//import { RxDBAjvValidatePlugin } from 'rxdb/plugins/ajv-validate';
import { RxDBValidateZSchemaPlugin } from 'rxdb/plugins/validate-z-schema';
import { BehaviorSubject, Subscription, firstValueFrom, forkJoin } from 'rxjs';
import { Injectable, isDevMode } from '@angular/core';
import * as PouchdbAdapterHttp from 'pouchdb-adapter-http';
import * as PouchdbAdapterIdb from 'pouchdb-adapter-idb';
import { AppState } from '../../app-state';
import { take, filter, first, skipWhile } from 'rxjs/operators';
import { EventEmitter } from 'events';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TestbedRxdbState } from './testbed-rxdb-state'
import { TestbedRxdbCollectionService } from './testbed-rxdb-collection.service'
import { RxCollectionCreatorBase } from 'rxdb/dist/types/types';
import { BuildingService } from '../building.service';
import { PointTestLogService } from '../point-test-log.service';
import { PathTestLogService } from '../path-test-log.service';
import { LookupService } from '../lookup.service';
import { PathTestService } from '../path-test.service';
import { UserService } from '../user.service';
import { PointService } from '../point.service';
import { PathWaypointService } from '../path-waypoint.service';
import { PathViewService } from '../path-view.service';
import { ScoutTaskService } from '../scout-task.service';
import { ScoutPointPhotoService } from '../scout-point-photo.service';
import { StageService } from '../stage.service';
import { RolePermissionViewService } from '../role-permission-view.service';
import { TestbedRxdbGraphQlCollectionService } from './testbed-rxdb-graphql-collection.service';
import { TestbedRxdbError } from './testbed-rxdb-error';
import { TestSeqService } from '../test-seq.service';
import { AppErrorMessageEvent } from '../../app-events';
import Dexie from 'dexie';
import { DriveTestService } from '../drive-test.service';
import { RouteService } from '../route.service';
import { DriveTestSeqService } from '../drive-test-seq.service';
import {PathRxdbService} from "../path-rxdb.service";
import { AntennaService } from '../antenna.service';

@Injectable({ providedIn: 'root' })
export class TestbedRxdbService {

    rxdb : RxDatabase

    collectionServices : TestbedRxdbCollectionService<any,any>[] = []


    constructor(
            protected appState : AppState,
            protected rxdbState : TestbedRxdbState,
            protected modalService: BsModalService,
            buildingService: BuildingService,
            routeService: RouteService,
            protected pointTestLogService: PointTestLogService,
            protected pathService:PathRxdbService,
            protected pathTestLogService: PathTestLogService,
            protected pointService: PointService,
            protected pathWaypointService: PathWaypointService,
            protected pathViewService: PathViewService,
            lookupService: LookupService,
            driveTestService: DriveTestService,
            pathTestService: PathTestService,
            protected scoutTaskService: ScoutTaskService,
            userService: UserService,
            protected scoutPointPhotoService: ScoutPointPhotoService,
            testSeqService: TestSeqService,
            driveTestSeqService: DriveTestSeqService,
            stageService: StageService,
            rolePermissionViewService: RolePermissionViewService,
            antennaService: AntennaService
             ) {
        EventEmitter.defaultMaxListeners = TestbedRxdbCollectionService.MAX_LISTENERS
        this.collectionServices = [
            buildingService,
            routeService,
            pointTestLogService,
            pathTestLogService,
            pointService,
            pathWaypointService,
            pathViewService,
            lookupService,
            pathTestService,
            driveTestService,
            scoutTaskService,
            userService,
            scoutPointPhotoService,
            testSeqService,
            driveTestSeqService,
            stageService,
            rolePermissionViewService,
            pathService,
            antennaService
        ]

        const haveLiveGraphQlCollections = this.collectionServices.some( service => service.syncConfig?.bGraphQL && service.syncConfig.intervalMs == 0 )
        if ( haveLiveGraphQlCollections ) {
            TestbedRxdbGraphQlCollectionService.initWebSocketInstance(this.appState.settings.getGraphQLWsUrl())
        }
        this.initDb()
    }


    async initDb(bInitRxdb:boolean=true) {
        addRxPlugin(PouchdbAdapterIdb)
        addRxPlugin(PouchdbAdapterHttp)
        addRxPlugin(RxDBQueryBuilderPlugin)
        addRxPlugin(RxDBReplicationPlugin)
        addRxPlugin(RxDBReplicationGraphQLPlugin)
        addRxPlugin(RxDBLeaderElectionPlugin)
        if ( isDevMode() ) {
            //addRxPlugin(RxDBValidatePlugin)
            //addRxPlugin(RxDBAjvValidatePlugin); //Reports specific field for VD2 additional properties errors (RxDBValidatePlugin does NOT)
            addRxPlugin(RxDBValidateZSchemaPlugin)
        }
        console.log("Initializing testbed rxdb...")

        let dbNamePrefix = this.appState.settings.getEnvName().toLowerCase() + "_" // make sure dev changes don't get synced to prod or stage
        const additionalPrefix = this.appState.settings.getRxdbUniquePrefix()
        if ( additionalPrefix ) {
           dbNamePrefix += additionalPrefix
        }
        let databaseInitOk = true
        let initRepairError: any = null;

        const initCollections = async ( collectionServices : TestbedRxdbCollectionService<any,any>[], rxdb: RxDatabase ) => {
            let collectionConfigs : { [name:string]: RxCollectionCreatorBase } = {}
            collectionServices.forEach(cs=>collectionConfigs[cs.name] = cs.buildRxdbConfig())
            try{
                await rxdb.addCollections( collectionConfigs )
            }
            catch (error){
                console.error(error);
                const paramName = error?.parameters?.name;
                if (paramName && paramName!='conflict')
                {
                        let initialState = {
                            onNo: () => {
                                const reason = TestbedRxdbError.extractMsg(error);
                                initRepairError = new Error(reason)
                                this.appState.appExitedMsg = "Rxdb Initialization failed."
                                this.appState.appExitedSubject.next(true)
                            },
                            onYes: async () => {
                                this.appState.publishInfo(`A change is made in ${paramName}`)
                                try {
                                    await rxdb.removeCollection(paramName)
                                    await initCollections(collectionServices, rxdb)
                                    window.location.reload()
                                } catch (error) {
                                    const reason = TestbedRxdbError.extractMsg(error);
                                    initRepairError = error;
                                    this.appState.appExitedMsg = `Rxdb collection '${paramName}' rebuild error. ${reason}`
                                    this.appState.appExitedSubject.next(true)
                                }
                            },
                            question: `Would you like to rebuild the ${paramName} Schema
                    <table class="m-3 font-italic small">
                    <tr><td class="alert-warning font-weight-bold">Warning</td><td>Local edits will be lost.</td></tr></table>`,
                            title: 'Rxdb Initialization Failure',
                            focusOnNo: true,
                            yesStyle: 'btn btn-danger'
                        }
                     const modalRef = this.modalService.show(ConfirmModalComponent, { ignoreBackdropClick: true, initialState });

                     // wait for modal result so setRxdbMgrInitialized will not be called too soon
                     await firstValueFrom(modalRef.onHidden.pipe(first()))
                     if ( initRepairError ) {
                        throw initRepairError;
                     }
                }
                else {
                    if (error.message.includes('transaction') || error.parameters.name!='conflict')
                    {
                        await initCollections(collectionServices,rxdb)
                    }
                    else throw "Not a name"
                }
            }
            collectionServices.forEach(cs=>cs.rxCollection = rxdb.collections[cs.name])
        }

        if ( bInitRxdb )
        try {

            this.rxdb = await createRxDatabase({
                name: dbNamePrefix + 'testbed',
                adapter: 'idb',
                multiInstance: false,
                eventReduce: false,
                options: {
                    auto_compact: false
                }
            })

            await this.deleteOldLocalPhotosDb(dbNamePrefix)

            //const pdb : any = this.rxdb.internalStore
            //pdb.setMaxListeners(TestbedRxdbService.MAX_LISTENERS)
            await initCollections(this.collectionServices,this.rxdb)

            // rxdbState is primarily for sync state tracking (collections in localRxdb not added)
            this.rxdbState.collectionServices = this.collectionServices

        } catch ( error ) {
            databaseInitOk = false
            console.error(error)
            this.publishError(error)
        }


        if ( databaseInitOk ) {
            this.collectionServices.forEach(service=>service.resetReplicationStaleSubject())
            console.log("testbed rxdb ready")
            this.appState.setRxdbMgrInitialized(true)
            this.rxdbState.readySubject.next(true)
        } else if ( !initRepairError ) {
            const onlineStatus = this.appState.isOffline() ?
                'Server is not reachable and application may not be usable until remote data can be reloaded.'
                : 'Reloading from server may take several minutes.'
            const onlineStatusWarning =
                `<tr><td class="p-2"> </td></tr>
                <tr>
                    <td class="alert-warning font-weight-bold">Warning</td>
                    <td>${onlineStatus}</td>
                </tr>`

            let initialState = {
                onNo: () => {
                    this.appState.appExitedMsg = "Rxdb Initialization failed."
                    this.appState.appExitedSubject.next(true)
                },
                onYes: async () => {
                    this.appState.publishInfo('Rebuilding local database (this may take some time and requires remote connection)')
                    await this.stopSyncs()
                    console.warn('Removing old rxdb database: ' + this.rxdb?.name)
                    try {
                        if ( this.rxdb && !databaseInitOk ) {
                            await this.rxdb.remove()
                            await this.rxdb.destroy()
                        }
                    } catch ( error ) {
                        console.error('Failed removing old rxdb database')
                        console.error(error)
                        this.publishError(error)
                    }
                    this.initDb(!databaseInitOk)
                },
                question: `Would you like to rebuild the local RXDB database?
                    <table class="m-3 font-italic small">
                    <tr><td class="alert-warning font-weight-bold">Warning</td><td>Local edits will be lost.</td></tr>
                    ${onlineStatusWarning}</table>`,
                title: 'Rxdb Initialization Failure',
                focusOnNo: true,
                yesStyle: 'btn btn-danger'
            }
            this.modalService.show(ConfirmModalComponent, { ignoreBackdropClick: true, initialState });
        }
    }



    startSyncSubcriptions() {

        // NOTE: add ReplicationStateListeners before calling startSyncSubscriptions

        this.appState.userSubject.subscribe( user => {
            if ( user ) {
                this.rxdbState.readySubject.pipe( filter(ready=>ready), take(1) ).subscribe( _ => this.startSyncs() )
            } else {
                this.rxdbState.readySubject.pipe( filter(ready=>ready), take(1) ).subscribe( _ => this.stopSyncs() )
            }
        })
        this.appState.appExitedSubject.pipe( filter(bExit=>bExit), take(1) ).subscribe( _ => {
            try {
                this.stopSyncs()
            } catch ( error ) {
                console.error('Failed stopping rxdb syncs (they may have never started)')
            }
        })
    }


    async startSyncs() {
        this.stoppingSyncs.pipe(skipWhile( bStopping => bStopping ),take(1)).subscribe( async bStopping =>{
            console.log('***** STARTING rxdb collection syncs with CouchDb and GraphQL *****')
            for( const collectionService of this.collectionServices ) {
                if ( collectionService.syncConfig ) {
                    await collectionService.startSync()
                }
            }
        })
    }


    stoppingSyncs = new BehaviorSubject<boolean>(false)
    async stopSyncs() {
        this.stoppingSyncs.next(true)
        console.log('***** STOPPING rxdb collection syncs with CouchDb *****')
        for( const collectionService of this.collectionServices ) {
            console.log('Stopping ' + collectionService.name)
            collectionService.bStopped = true
            await collectionService.replicationState?.cancel()
            collectionService.bActiveSync = false
        }
        this.rxdbState.updateActiveSyncStatus()
        console.log('***** STOPPED rxdb collection syncs with CouchDb *****')
        this.stoppingSyncs.next(false)
    }


    doWhenReady( readyFn : () => void ) : Subscription {
        if ( this.rxdb ) {
            readyFn()
        } else {
            const s = this.rxdbState.readySubject.subscribe(
                bReady => {
                    if ( bReady ) {
                        readyFn()
                        s.unsubscribe()
                    }
                }
            )
            return s
        }
        return null
    }

    publishError(error: any): string {
        let errMsg = TestbedRxdbError.extractMsg(error)
        return this.appState.publishErrorObj(error,errMsg,this,AppErrorMessageEvent.DEFAULT_EXPIRE_SECS)
    }


    //
    // This is for v0.1.21
    // Remove this method when sure no one has local scout photos not uploaded with old client
    //
    async deleteOldLocalPhotosDb(dbNamePrefix:string) {
        const oldLocalDbName = `_pouch_${dbNamePrefix}testbed_local-rxdb-0-_rxdb_internal`
        const oldLocalDbExists = await Dexie.exists( oldLocalDbName)
        console.log(`oldLocalDbName=${oldLocalDbName} exists:${oldLocalDbExists}`)
        if ( oldLocalDbExists ) {
            let initialState = {
                yesBtnText: 'Delete',
                noBtnText: 'Cancel',
                onYes: async () => {
                    this.appState.publishInfo('Removing old scout photos local cache')
                    console.warn('Removing old scout photos local cache: ' +oldLocalDbName)
                    try {
                       await removeRxDatabase(dbNamePrefix + 'testbed_local','idb')
                    } catch ( error ) {
                        console.error('Failed removing old rxdb database: ' + dbNamePrefix + 'testbed_local')
                        console.error(error)
                        this.publishError(error)
                    }
                },
                question: `Delete old scout photo cache to save space?
                    <div class="font-italic text-danger">
                        <i class="fa fa-exclamation-triangle mr-2 mt-2"></i>Scout photos not yet uploaded will be lost
                    </div>`,
                title: 'Scout Photo Cache Cleanup',
                focusOnNo: true,
                yesStyle: 'btn btn-danger'
            }
            const modalRef = this.modalService.show(ConfirmModalComponent, { ignoreBackdropClick: true, initialState });
            await firstValueFrom(modalRef.onHidden.pipe(first()));
        }
    }

    primeIndices = async () => {
        const queries = [
            this.pointTestLogService.rxCollection.find().where('taskId').eq('!'),
            this.pathTestLogService.rxCollection.find().where('taskId').eq('!'),
            this.scoutPointPhotoService.rxCollection.find().where('scoutPointId').eq('!'),
            this.scoutTaskService.rxCollection.find().where('buildingId').eq('!'),
            this.pathWaypointService.rxCollection.find().where('pathId').eq('!'),
            this.pointService.rxCollection.find().where('buildingId').eq('!')

            //TODO Find more rxCollection schemas that have indexes and add find where calls            
        ]
        await firstValueFrom(forkJoin(queries.map(q=>q.exec())));
    }

}
